import GuideLayout, {
  H2,
  H3,
  P,
  UL,
  Callout,
  Strong,
  Em,
} from "../ui/GuideLayout.jsx";

export default function Glossario() {
  return (
    <GuideLayout
      title="Glossário japonês-português para dekassegui"
      subtitle="Termos que todo brasileiro no Japão escuta no trabalho, na fábrica, na imigração, na prefeitura e no banco — traduzidos e explicados em português."
      slug="glossario-japones-portugues"
      description="Glossário de termos japoneses comuns para brasileiros no Japão. Trabalho, impostos, fábrica, imigração, banco, vida diária."
      updated="Maio de 2026"
      readTime="14 min"
    >
      <P>
        Este é um dicionário prático dos termos japoneses que aparecem na
        vida do dekassegui. Não está em ordem alfabética — está agrupado por
        contexto, do mais comum ao mais específico. Use o Ctrl+F para
        procurar um termo específico.
      </P>

      <H2 id="trabalho-fabrica">Trabalho e fábrica</H2>

      <P>
        <Strong>派遣 (はけん / hakken)</Strong> — Empreiteira. Você é
        funcionário formal da empreiteira, que &ldquo;aluga&rdquo; você pra
        fábrica. 99% dos brasileiros começam aqui.
      </P>

      <P>
        <Strong>派遣会社 (はけんがいしゃ / hakken gaisha)</Strong> — A empresa
        empreiteira em si.
      </P>

      <P>
        <Strong>正社員 (せいしゃいん / seishain)</Strong> — Funcionário efetivo,
        contratado direto pela fábrica. Tem bônus, plano de carreira,
        estabilidade.
      </P>

      <P>
        <Strong>契約社員 (けいやくしゃいん / keiyaku shain)</Strong> —
        Funcionário com contrato direto mas por tempo determinado (1 ano,
        renovável).
      </P>

      <P>
        <Strong>パート (paato) / アルバイト (arubaito)</Strong> — Meio-período,
        trabalho parcial.
      </P>

      <P>
        <Strong>残業 (ざんぎょう / zangyo)</Strong> — Hora extra. Paga 25% a
        mais que a hora normal.
      </P>

      <P>
        <Strong>夜勤 (やきん / yakin)</Strong> — Turno noturno (entre 22h e
        5h). Paga 25% a mais.
      </P>

      <P>
        <Strong>日勤 (にっきん / nikkin)</Strong> — Turno diurno.
      </P>

      <P>
        <Strong>休日出勤 (きゅうじつしゅっきん / kyujitsu shukkin)</Strong> —
        Trabalho em feriado. Paga 35% a mais.
      </P>

      <P>
        <Strong>有給休暇 (ゆうきゅうきゅうか / yuukyu kyuuka)</Strong> — Férias
        remuneradas. Por lei: 10 dias depois de 6 meses trabalhados, aumenta
        com tempo de serviço.
      </P>

      <P>
        <Strong>給料 (きゅうりょう / kyuuryou)</Strong> — Salário.
      </P>

      <P>
        <Strong>給料明細 (きゅうりょうめいさい / kyuuryou meisai)</Strong> —
        Contracheque. Mostra detalhamento do salário, descontos e horas.
      </P>

      <P>
        <Strong>時給 (じきゅう / jikyuu)</Strong> — Valor da hora trabalhada.
      </P>

      <P>
        <Strong>月給 (げっきゅう / gekkyuu)</Strong> — Salário mensal fixo.
      </P>

      <P>
        <Strong>ボーナス (boonasu)</Strong> — Bônus. Tipicamente verão (junho)
        e inverno (dezembro), 1-3 meses cada.
      </P>

      <P>
        <Strong>班長 (はんちょう / han-chou)</Strong> — Líder de turno.
        Posição alcançável pra brasileiro após anos de experiência.
      </P>

      <P>
        <Strong>職長 (しょくちょう / shokuchou)</Strong> — Chefe de área.
        Acima do han-chou.
      </P>

      <P>
        <Strong>作業長 (さぎょうちょう / sagyo-chou)</Strong> — Encarregado de
        produção, acima do shokuchou.
      </P>

      <P>
        <Strong>朝礼 (ちょうれい / chourei)</Strong> — Reunião matinal antes
        do turno. Aviso de segurança, qualidade, recados.
      </P>

      <P>
        <Strong>体操 (たいそう / taisou)</Strong> — Ginástica laboral antes
        do turno. Obrigatória em muitas fábricas.
      </P>

      <P>
        <Strong>安全 (あんぜん / anzen)</Strong> — Segurança.
      </P>

      <P>
        <Strong>品質 (ひんしつ / hinshitsu)</Strong> — Qualidade.
      </P>

      <P>
        <Strong>5S (ファイブエス)</Strong> — Os 5 esses: seiri (organizar),
        seiton (ordenar), seiso (limpar), seiketsu (padronizar), shitsuke
        (disciplinar). Filosofia padrão de qualquer fábrica japonesa.
      </P>

      <P>
        <Strong>QCD (キューシーディー)</Strong> — Quality, Cost, Delivery —
        Qualidade, Custo, Prazo. As 3 prioridades de produção.
      </P>

      <P>
        <Strong>ホウレンソウ (hourensou)</Strong> — Acrônimo: <Em>houkoku</Em>{" "}
        (relatar), <Em>renraku</Em> (comunicar), <Em>soudan</Em> (consultar).
        Filosofia de comunicação japonesa.
      </P>

      <H2 id="impostos">Impostos e seguros</H2>

      <P>
        <Strong>社会保険 (しゃかいほけん / shakai hoken)</Strong> — Seguro
        social da empresa. Inclui saúde, aposentadoria e seguro-desemprego.
        Desconto ~15% do salário.
      </P>

      <P>
        <Strong>国民健康保険 (こくみんけんこうほけん / kokumin kenko hoken)</Strong> —
        Seguro saúde nacional. Para quem não tem shakai (autônomos,
        desempregados). Pago direto pra prefeitura.
      </P>

      <P>
        <Strong>厚生年金 (こうせいねんきん / kosei nenkin)</Strong> —
        Aposentadoria de funcionário (parte do shakai hoken).
      </P>

      <P>
        <Strong>国民年金 (こくみんねんきん / kokumin nenkin)</Strong> —
        Aposentadoria nacional, para autônomos. ¥17.510/mês em 2025.
      </P>

      <P>
        <Strong>所得税 (しょとくぜい / shotokuzei)</Strong> — Imposto de
        renda nacional. Progressivo, 5% a 45%.
      </P>

      <P>
        <Strong>住民税 (じゅうみんぜい / juushinkoku)</Strong> — Imposto
        residencial. ~10% da renda do ano anterior. Pago à prefeitura.{" "}
        <Strong>Não cobra no primeiro ano</Strong> de residência.
      </P>

      <P>
        <Strong>確定申告 (かくていしんこく / kakutei shinkoku)</Strong> —
        Declaração anual de imposto (equivalente ao IR brasileiro). Entre 16
        de fevereiro e 15 de março.
      </P>

      <P>
        <Strong>年末調整 (ねんまつちょうせい / nenmatsu chosei)</Strong> —
        Ajuste de fim de ano feito pela empresa em dezembro. Quem tem só um
        emprego geralmente não precisa fazer kakutei shinkoku.
      </P>

      <P>
        <Strong>源泉徴収票 (げんせんちょうしゅうひょう / gensen choshuhyo)</Strong> —
        Comprovante de rendimentos do ano. Empresa fornece em janeiro.
      </P>

      <P>
        <Strong>ふるさと納税 (ふるさとのうぜい / furusato nozei)</Strong> —
        &ldquo;Doação&rdquo; pra município, com dedução quase total no imposto.
        Você recebe presentes (comida, eletrodomésticos) em troca. Custo
        efetivo: ¥2.000/ano.
      </P>

      <P>
        <Strong>iDeCo (イデコ)</Strong> — Aposentadoria privada com dedução
        fiscal. Recomendável para quem vai ficar mais de 10 anos.
      </P>

      <P>
        <Strong>脱退一時金 (だったいいちじきん / dattai ichijikin)</Strong> —
        Reembolso parcial da aposentadoria ao sair definitivamente do Japão.
        Veja a <a href="/calculadoras/aposentadoria" className="text-accent underline">calculadora</a>.
      </P>

      <P>
        <Strong>20.42%</Strong> — A famosa taxa de imposto que é descontada
        do dattai ichijikin. Recuperável via nouzei kanrinin.
      </P>

      <P>
        <Strong>納税管理人 (のうぜいかんりにん / nouzei kanrinin)</Strong> —
        &ldquo;Representante fiscal&rdquo;. Pessoa que você nomeia ANTES de
        sair do Japão para recuperar imposto do dattai ichijikin.
      </P>

      <H2 id="imigracao">Imigração e visto</H2>

      <P>
        <Strong>在留カード (ざいりゅうカード / zairyu card)</Strong> — Cartão
        de residência. Documento que prova seu status legal no Japão. Vence
        a cada 1-7 anos.
      </P>

      <P>
        <Strong>在留資格 (ざいりゅうしかく / zairyu shikaku)</Strong> — Status
        de residência. Seu &ldquo;tipo&rdquo; de visto.
      </P>

      <P>
        <Strong>永住者 (えいじゅうしゃ / eijuusha)</Strong> — Residente
        permanente. Sem prazo de validade do status. Cartão renova a cada 7
        anos.
      </P>

      <P>
        <Strong>永住権 (えいじゅうけん / eijuuken)</Strong> — &ldquo;Direito
        de residência permanente&rdquo;. O que se aplica e o que se conquista.
      </P>

      <P>
        <Strong>定住者 (ていじゅうしゃ / teijuusha)</Strong> — Long-term
        resident. Status comum pra descendente de japonês e cônjuge.
        Trabalho livre sem restrição.
      </P>

      <P>
        <Strong>家族滞在 (かぞくたいざい / kazoku taizai)</Strong> — Visto de
        dependente. Cônjuge ou filho de trabalhador. Trabalho limitado a 28h/sem.
      </P>

      <P>
        <Strong>出入国在留管理庁 (しゅつにゅうこくざいりゅうかんりちょう)</Strong>{" "}
        — Immigration Services Agency (ISA). Onde você renova visto.
      </P>

      <P>
        <Strong>特定在留カード (とくていざいりゅうカード)</Strong> — Novo
        cartão integrado (zairyu + My Number) sendo lançado em junho/2026.
      </P>

      <P>
        <Strong>マイナンバー (mai-namba)</Strong> — My Number. Número
        equivalente ao CPF, usado pra impostos, saúde, INSS.
      </P>

      <H2 id="prefeitura-banco">Prefeitura e banco</H2>

      <P>
        <Strong>市役所 (しやくしょ / shiyakusho)</Strong> — Prefeitura
        municipal.
      </P>

      <P>
        <Strong>区役所 (くやくしょ / kuyakusho)</Strong> — Prefeitura de
        bairro (em cidades grandes como Tóquio).
      </P>

      <P>
        <Strong>町役場 (まちやくば / machi yakuba)</Strong> — Prefeitura de
        vila/cidade pequena.
      </P>

      <P>
        <Strong>住民票 (じゅうみんひょう / juuminhyou)</Strong> — &ldquo;Certidão
        de residência&rdquo;. Documento que prova onde você mora. Usado pra
        tudo (imigração, aluguel, casamento).
      </P>

      <P>
        <Strong>住民登録 (じゅうみんとうろく / juumin touroku)</Strong> —
        Registro de residência. Fazer ao mudar de endereço.
      </P>

      <P>
        <Strong>転入届 (てんにゅうとどけ / tennyuu todoke)</Strong> —
        Notificação de entrada na cidade. Faz em 14 dias após mudar.
      </P>

      <P>
        <Strong>転出届 (てんしゅつとどけ / tenshutsu todoke)</Strong> —
        Notificação de saída da cidade. Faz ANTES de mudar.
      </P>

      <P>
        <Strong>印鑑 (いんかん / inkan) / 判子 (はんこ / hanko)</Strong> —
        Carimbo pessoal. Substitui assinatura no Japão.
      </P>

      <P>
        <Strong>銀行印 (ぎんこういん / ginko-in)</Strong> — Carimbo bancário,
        registrado no banco.
      </P>

      <P>
        <Strong>実印 (じついん / jitsuin)</Strong> — Carimbo registrado em
        cartório. Pra carro, imóvel.
      </P>

      <P>
        <Strong>ゆうちょ銀行 (yuucho ginko)</Strong> — Banco postal japonês.
        Mais fácil de abrir conta pra estrangeiro recém-chegado.
      </P>

      <P>
        <Strong>普通預金 (ふつうよきん / futsu yokin)</Strong> — Conta
        corrente. O tipo padrão de conta.
      </P>

      <P>
        <Strong>振込 (ふりこみ / furikomi)</Strong> — Transferência bancária.
      </P>

      <H2 id="moradia">Moradia</H2>

      <P>
        <Strong>アパート (apaato / apato)</Strong> — Apartamento em prédio
        baixo (2-3 andares), estrutura de madeira ou leve. Mais barato.
      </P>

      <P>
        <Strong>マンション (manshon)</Strong> — Apartamento em prédio de
        concreto (4+ andares). Mais caro, melhor isolamento.
      </P>

      <P>
        <Strong>敷金 (しききん / shikikin)</Strong> — Depósito caução. 1-2
        meses de aluguel. Devolvido no fim do contrato.
      </P>

      <P>
        <Strong>礼金 (れいきん / reikin)</Strong> — &ldquo;Dinheiro de
        presente&rdquo; pro proprietário. 1-2 meses. <Strong>Não devolvido</Strong>.
      </P>

      <P>
        <Strong>家賃 (やちん / yachin)</Strong> — Aluguel mensal.
      </P>

      <P>
        <Strong>管理費 (かんりひ / kanrihi)</Strong> — Taxa de condomínio.
      </P>

      <P>
        <Strong>保証会社 (ほしょうがいしゃ / hoshou gaisha)</Strong> — Empresa
        garantidora. Substitui o fiador.
      </P>

      <P>
        <Strong>不動産 (ふどうさん / fudousan)</Strong> — Imobiliária.
      </P>

      <H2 id="documentos">Documentos</H2>

      <P>
        <Strong>免許証 (めんきょしょう / menkyo-shou)</Strong> — Carteira de
        motorista japonesa.
      </P>

      <P>
        <Strong>外免切替 (がいめんきりかえ / gaimen kirikae)</Strong> —
        Conversão de carteira estrangeira para japonesa. Veja{" "}
        <a href="/guias/gaimen-kirikae" className="text-accent underline">
          guia específico
        </a>.
      </P>

      <P>
        <Strong>パスポート (pasupooto)</Strong> — Passaporte.
      </P>

      <P>
        <Strong>翻訳 (ほんやく / honyaku)</Strong> — Tradução.
      </P>

      <P>
        <Strong>戸籍謄本 (こせきとうほん / koseki touhon)</Strong> — Equivalente
        à certidão de família (só pra cidadão japonês).
      </P>

      <H2 id="vida-diaria">Vida diária</H2>

      <P>
        <Strong>コンビニ (konbini)</Strong> — Loja de conveniência (7-Eleven,
        Lawson, FamilyMart). Você paga conta lá, faz cópia, retira
        encomenda, tudo.
      </P>

      <P>
        <Strong>ドラッグストア (drug store)</Strong> — Farmácia + bazar +
        cosméticos + às vezes alimentos. Mat-kiyo, Welcia, Sundrug, Tsuruha,
        Cosmos.
      </P>

      <P>
        <Strong>ホームセンター (home center)</Strong> — Loja de materiais de
        construção, ferramentas, jardinagem, casa. Comeri, Cainz, Kohnan.
      </P>

      <P>
        <Strong>役所 (やくしょ / yakusho)</Strong> — Repartição pública em
        geral.
      </P>

      <P>
        <Strong>病院 (びょういん / byouin)</Strong> — Hospital.
      </P>

      <P>
        <Strong>クリニック (clinic)</Strong> — Clínica menor.
      </P>

      <P>
        <Strong>診察 (しんさつ / shinsatsu)</Strong> — Consulta médica.
      </P>

      <P>
        <Strong>処方箋 (しょほうせん / shohousen)</Strong> — Receita médica.
      </P>

      <P>
        <Strong>薬局 (やっきょく / yakkyoku)</Strong> — Farmácia onde compra
        com receita.
      </P>

      <P>
        <Strong>救急車 (きゅうきゅうしゃ / kyukyuusha)</Strong> — Ambulância.
        Chama discando 119. Gratuita.
      </P>

      <P>
        <Strong>110番 (hyaku-tou-ban)</Strong> — Polícia. Discar 110.
      </P>

      <H2 id="cultura-trabalho">Cultura de trabalho</H2>

      <P>
        <Strong>お疲れ様 (おつかれさま / otsukaresama)</Strong> — Literalmente
        &ldquo;você está cansado&rdquo;. Usado como &ldquo;bom trabalho&rdquo;
        ou despedida no trabalho.
      </P>

      <P>
        <Strong>お先に失礼します (おさきにしつれいします / osaki ni shitsurei shimasu)</Strong>{" "}
        — &ldquo;Vou sair antes, com licença&rdquo;. Dizer ao sair do trabalho
        antes dos colegas.
      </P>

      <P>
        <Strong>ありがとうございます (arigatou gozaimasu)</Strong> — Muito
        obrigado (formal).
      </P>

      <P>
        <Strong>すみません (sumimasen)</Strong> — Desculpa / com licença /
        obrigado dependendo do contexto. O termo mais útil pra brasileiro
        no Japão.
      </P>

      <P>
        <Strong>分かりました (わかりました / wakarimashita)</Strong> —
        &ldquo;Entendi&rdquo;.
      </P>

      <P>
        <Strong>頑張ります (がんばります / ganbarimasu)</Strong> — &ldquo;Vou
        me esforçar&rdquo;. Resposta padrão a praticamente qualquer
        instrução do chefe.
      </P>

      <P>
        <Strong>大丈夫です (だいじょうぶです / daijoubu desu)</Strong> —
        &ldquo;Tudo bem / não precisa&rdquo;.
      </P>

      <Callout>
        <Strong>Dica final:</Strong> aprender estes 80 termos resolve 90%
        das situações do dia a dia. Para os 10% restantes, Google Translate
        no celular salva. Não tenha vergonha de usar.
      </Callout>
    </GuideLayout>
  );
}
